import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  randPastDate,
  randRecentDate,
  randNumber,
  randChanceBoolean,
  randProductName,
  randParagraph,
} from '@ngneat/falso'

import type { SchemaAttributes } from '../../types.js'
import type { MockServer } from '../server.js'
import { getExistingEntity } from '../utils.js'

type ConsultationProcedure = SchemaAttributes<'ConsultationProcedure'>

export const model = (Model as ModelDefinition<ConsultationProcedure>).extend({
  createdUser: belongsTo('user'),
  modifiedUser: belongsTo('user'),
  supervisingVeterinarian: belongsTo('user'),
  performedBy: belongsTo('user'),
  patient: belongsTo('patient'),
})

export const factory = Factory.extend<ConsultationProcedure>({
  code() {
    return 'PROC-'.concat(String(randNumber({ min: 1000, max: 9999 })))
  },
  name() {
    return randProductName()
  },
  used() {
    return randChanceBoolean({ chanceTrue: 0.8 })
      ? randRecentDate().toISOString()
      : null
  },
  created() {
    return randPastDate().toISOString()
  },
  modified() {
    return randRecentDate().toISOString()
  },
  herd_size_multiplier() {
    return randChanceBoolean({ chanceTrue: 0.3 })
      ? randNumber({ min: 1, max: 10 })
      : null
  },
  display_price() {
    return randNumber({ min: 10, max: 500, precision: 2 })
  },
  hidden_items_display_price() {
    return randChanceBoolean({ chanceTrue: 0.2 })
      ? randNumber({ min: 5, max: 50, precision: 2 })
      : 0
  },
  is_minimum_charge_active() {
    return randChanceBoolean({ chanceTrue: 0.3 })
  },
  is_cabinet_item() {
    return randChanceBoolean({ chanceTrue: 0.5 })
  },
  quantity() {
    return randNumber({ min: 1, max: 5, precision: 1 })
  },
  duration() {
    return randChanceBoolean({ chanceTrue: 0.6 })
      ? randNumber({ min: 15, max: 120 })
      : null
  },
  description() {
    return randChanceBoolean({ chanceTrue: 0.7 })
      ? randParagraph({ maxCharCount: 150 })
      : null
  },

  afterCreate(procedure, server) {
    if (!procedure.createdUser) {
      const createdUser = getExistingEntity(server, 'user')
      procedure.update({ createdUser })
    }

    if (!procedure.modifiedUser) {
      const modifiedUser = getExistingEntity(server, 'user')
      procedure.update({ modifiedUser })
    }
  },
})

export function routes(server: MockServer) {
  server.get('/consultation_procedures/:id', (schema, request) => {
    const { id } = request.params
    return schema.find('consultationProcedure', id)
  })

  server.post(
    '/consultations/:consultation_pk/procedures/:org_item_pk/create/',
    function (schema, request) {
      const { data } = JSON.parse(request.requestBody)
      const { consultation_pk, org_item_pk } = request.params
      const { attributes, relationships } = data

      const supervisingVeterinarianId =
        relationships?.supervising_veterinarian?.data?.id
      const performedById = relationships?.performed_by?.data?.id
      const patientIds = relationships?.patients?.data?.map(
        (p: { id: string }) => p.id,
      )

      const procedure = schema.create('consultationProcedure', {
        ...attributes,
        consultation_pk: parseInt(consultation_pk, 10),
        org_item_pk: parseInt(org_item_pk, 10),
      })

      if (supervisingVeterinarianId) {
        const supervisingVet = schema.find('user', supervisingVeterinarianId)
        if (supervisingVet) {
          procedure.update({ supervisingVeterinarian: supervisingVet })
        }
      }

      if (performedById) {
        const performedBy = schema.find('user', performedById)
        if (performedBy) {
          procedure.update({ performedBy })
        }
      }

      if (patientIds && patientIds.length > 0) {
        const patient = schema.find('patient', patientIds[0])
        if (patient) {
          procedure.update({ patient })
        }
      }

      const consultation = schema.find('consultation', consultation_pk)
      const organizationItem = schema.find('organizationItem', org_item_pk)

      schema.create('consultationItemOverview', {
        id: procedure.id,
        code: organizationItem?.code || procedure.code,
        name: organizationItem?.name || procedure.name,
        type_code: 'PROCEDURE',
        used: attributes.used,
        created: procedure.created,
        modified: procedure.modified,
        herd_size_multiplier: procedure.herd_size_multiplier,
        display_price: attributes.price,
        hidden_items_display_price: procedure.hidden_items_display_price || 0,
        is_minimum_charge_active: procedure.is_minimum_charge_active,
        display_amount: attributes.quantity,
        description: attributes.description,
        consultation,
        patient: procedure.patient,
        createdUser: procedure.createdUser,
        modifiedUser: procedure.modifiedUser,
        supervisingVeterinarian: procedure.supervisingVeterinarian,
      })

      return {
        data: [procedure.attrs],
      }
    },
  )
}
